import "./lib/i18n"; // must be first
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { LocationProvider } from "./lib/location";
import { fetchGeo } from "./lib/geo";
import { isDriverApp, getAppMode } from "./lib/app-mode";
import { registerPush } from "./lib/push";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import { Login, Register } from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import Services from "./pages/Services";
import BookService from "./pages/BookService";
import RideRequest from "./pages/RideRequest";
import { CustomerDashboard, SupplierDashboard, AdminDashboard } from "./pages/Dashboards";
import { PayPalReturn } from "./pages/Payment";
import MarketingLanding from "./pages/MarketingLanding";
import { About, Pricing, Contact } from "./pages/Pages";
import WalletPage from "./pages/Wallet";
import Support from "./pages/Support";
import AccountSettings from "./pages/AccountSettings";
import ChangePassword from "./pages/ChangePassword";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Estimate from "./pages/Estimate";
import AdminPrices from "./pages/AdminPrices";
import AdminPricingV2 from "./pages/AdminPricingV2";
import SharedRide from "./pages/SharedRide";
import SplitPay from "./pages/SplitPay";
import "@/App.css";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-16 text-center font-mono uppercase text-xs text-neutral-400">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const loc = useLocation();
  // Fire-and-forget: detect visitor's country/currency on first mount so the
  // Estimate + LuxuryVehiclePicker default to their local currency. Cached
  // in localStorage for 24h.
  useEffect(() => { fetchGeo(); }, []);
  // Boot-time capture of app-mode from ?app=driver query param. Cached so the
  // driver bundle always knows which UI to show even after SPA navigation.
  useEffect(() => { getAppMode(); }, []);
  // Register for push notifications (Capacitor-only; no-op on web).
  useEffect(() => { registerPush().catch(() => {}); }, []);
  if (loc.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Driver app opens directly on the supplier dashboard instead of the
  // marketing landing page. The passenger app keeps the Landing default.
  const rootElement = isDriverApp() ? <Navigate to="/supplier" replace /> : <Landing />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={rootElement} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:category" element={<Services />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/estimate" element={<Estimate />} />
        <Route path="/shared" element={<SharedRide />} />
        <Route path="/shared/:code" element={<SharedRide />} />
        <Route path="/split/:code" element={<SplitPay />} />
        <Route path="/admin/prices" element={<Protected roles={["admin"]}><AdminPrices /></Protected>} />
        <Route path="/admin/pricing-v2" element={<Protected roles={["admin"]}><AdminPricingV2 /></Protected>} />
        <Route path="/request" element={<Protected roles={["customer"]}><RideRequest /></Protected>} />
        <Route path="/request/:category" element={<Protected roles={["customer"]}><RideRequest /></Protected>} />
        <Route path="/about" element={<About />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/book/:listingId" element={<Protected roles={["customer"]}><BookService /></Protected>} />
        <Route path="/paypal/return" element={<PayPalReturn />} />
        <Route path="/marketing" element={<MarketingLanding />} />
        <Route path="/welcome" element={<MarketingLanding />} />
        <Route path="/customer" element={<Protected roles={["customer"]}><CustomerDashboard /></Protected>} />
        <Route path="/activity" element={<Protected roles={["customer"]}><CustomerDashboard /></Protected>} />
        <Route path="/wallet" element={<Protected><WalletPage /></Protected>} />
        <Route path="/support" element={<Support />} />
        <Route path="/account/password" element={<Protected><ChangePassword /></Protected>} />
        <Route path="/account" element={<Protected><AccountSettings /></Protected>} />
        <Route path="/supplier" element={<Protected roles={["supplier"]}><SupplierDashboard /></Protected>} />
        <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="dark" position="top-right" toastOptions={{
            style: { background: "#141414", border: "1px solid #2A2A2A", borderRadius: 14, color: "#fff", fontFamily: "Inter, system-ui", fontSize: "13px" },
          }} />
        </BrowserRouter>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;
